import {
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  HStack,
  IconButton,
  LightMode,
  Spacer,
  useBreakpoint,
  useBreakpointValue,
  useColorModeValue,
} from '@chakra-ui/react';
import { AnimatePresence } from 'framer-motion';
import { useAtomValue } from 'jotai';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { routes } from '../data/routes';
import { sessionAtom, useSession } from '../lib/Session';
import { colors } from '../Stitches';
import { Authenticated, UnAuthenticated } from './AuthGuards';
import { ColorModeSwitcher } from './ColorModeSwitcher';
import NavDropDown from './DropDown';
import Emoji from './Emoji';
import { ScrollFade } from './ScrollFade';

const useLandingPage = () => {
  const [onLandingPage, setOnLandingPage] = useState(false);
  const location = useLocation();
  const session = useAtomValue(sessionAtom);

  useEffect(() => {
    if (location.pathname === routes.root && !session) {
      setOnLandingPage(true);
    } else {
      setOnLandingPage(false);
    }
  }, [location, session, setOnLandingPage]);

  return { onLandingPage };
};

const HomeLink = ({ children }: { children: React.ReactNode }) => {
  const session = useAtomValue(sessionAtom);

  return session ? (
    <Link to={routes.root}>{children}</Link>
  ) : (
    <a href={routes.site.landingPage}>{children}</a>
  );
};

const LetterDesk = () => {
  const purple = useColorModeValue(colors.ldPurple, colors.dark.ldPurple);

  return (
    <HomeLink>
      <Heading as="h1" fontSize="4xl" color={purple}>
        LetterDesk
      </Heading>
    </HomeLink>
  );
};

const WriteALetter = ({ collapseHeader }: { collapseHeader?: boolean }) => {
  const emojiProps = { symbol: '✍️', label: 'hand holding pen' };

  const component = collapseHeader ? (
    <IconButton icon={<Emoji {...emojiProps} />} aria-label="Write a letter" />
  ) : (
    <Button
      size={collapseHeader ? 'sm' : undefined}
      color="blackAlpha.900"
      bgColor="whiteAlpha.900"
    >
      <Emoji {...emojiProps} spaceRight /> Write a letter
    </Button>
  );

  return (
    <Link to={routes.newLetter}>
      <LightMode>{component}</LightMode>
    </Link>
  );
};

export const Nav = () => {
  const { logOut } = useSession();
  const headerPadding = useBreakpointValue({ base: 3, sm: 8, md: 10 });

  const border = useColorModeValue(
    `1px solid ${colors.gray100}`,
    `1px solid ${colors.dark.whiteAlpha100}`
  );

  const responsiveBorder = useBreakpointValue({ base: undefined, sm: border });

  const breakPoint = useBreakpoint();
  const { onLandingPage } = useLandingPage();

  const collapseHeader = breakPoint === 'sm' || breakPoint === 'base';

  return (
    <ScrollFade direction="top" interpolationPoint={0.14}>
      <Grid
        templateColumns={'min-content 1fr min-content'}
        borderBottom={responsiveBorder}
        px={headerPadding}
        height={{ base: '64px', sm: '82px', md: 100 }}
      >
        <Flex height="100%" align="center" borderRight={responsiveBorder} pr={headerPadding}>
          <AnimatePresence mode="wait">{!onLandingPage ? <LetterDesk /> : null}</AnimatePresence>
        </Flex>
        <Spacer />
        <HStack spacing={collapseHeader ? 2 : 4}>
          <ColorModeSwitcher />
          <Authenticated noRedirect>
            <>
              <WriteALetter collapseHeader={collapseHeader} />
              <NavDropDown onLogOut={logOut} />
            </>
          </Authenticated>
          <UnAuthenticated>
            <Link to={routes.logIn}>
              <Button>Sign in</Button>
            </Link>
          </UnAuthenticated>
        </HStack>
      </Grid>
      <Box h={collapseHeader ? 8 : 12} />
    </ScrollFade>
  );
};
